import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {AsyncPipe, KeyValuePipe, NgClass, NgFor, NgIf, NgStyle} from '@angular/common';
import {combineLatest, of} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Q9TrackDirective} from '@q9elements/ui-kit/common';
import {ClientEnvService} from '../../../core/services/client-env.service';
import {VersionsService} from '../../../core/services/versions.service';
import {StylesService} from '../../../diagram-render/styles/styles.service';
import {IToolbarMode, ToolbarStateService} from '../toolbar-state.service';

interface HelpListInterface {
  [key: string]: string;
}

@Component({
  selector: 'app-help-dialog',
  templateUrl: './help-dialog.component.html',
  styleUrls: ['./help-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatButtonModule,
    MatTooltipModule,
    MatMenuModule,
    MatIconModule,
    NgFor,
    NgIf,
    NgStyle,
    NgClass,
    AsyncPipe,
    KeyValuePipe,
    TranslateModule,
    Q9TrackDirective
  ]
})
export class HelpDialogComponent implements OnInit {
  @Input() canEdit: boolean;
  @Input() strippedMode: boolean;

  viewModeHelpList: HelpListInterface = {};
  switchToEditHint: HelpListInterface = {};
  switchToViewHint: HelpListInterface = {};
  editModeHelpList: HelpListInterface = {};

  activityStyle$ = this.stylesSvc.activityStyle$.pipe(filter(data => Boolean(data)));

  isDraftVersion$ = this.versionsSvc.currVersion$.pipe(
    filter(Boolean),
    map(version => Boolean(version === 'Draft')) // todo: check if we can not use magic string
  );

  isStripped$ = this.toolbarStateService.toolbarState$.pipe(
    map((toolbarState: IToolbarMode) => toolbarState.isStripped)
  );

  helpList$ = combineLatest(
    this.toolbarStateService.toolbarState$,
    this.isDraftVersion$,
    of(this.clientEnvService.isMobile)
  ).pipe(map(streamData => this.selectHintList(streamData)));

  constructor(
    protected translateSvc: TranslateService,
    private stylesSvc: StylesService,
    private clientEnvSvc: ClientEnvService,
    private clientEnvService: ClientEnvService,
    private toolbarStateService: ToolbarStateService,
    private versionsSvc: VersionsService
  ) {}

  ngOnInit() {
    this.fillShortcuts(this.clientEnvService.clientOS.toUpperCase());
  }

  goToSupport() {
    window.open('https://support.elements.cloud', this.clientEnvSvc.anchorTarget);
  }

  selectHintList([tbState, isDraftVersion, isMobile]) {
    const {editMode, canEditDiagram, isTrainingMode, isStripped}: IToolbarMode = tbState;

    switch (true) {
      case isMobile: {
        return {};
      }

      case editMode:
        return {...this.editModeHelpList, ...this.switchToViewHint};

      case canEditDiagram && isDraftVersion && !isStripped && !isTrainingMode:
        return {...this.viewModeHelpList, ...this.switchToEditHint};

      default:
        return this.viewModeHelpList;
    }
  }

  fillShortcuts(osPrefix) {
    if (osPrefix.length > 0) {
      osPrefix += '_';
    }

    this.viewModeHelpList = {
      [this.translateSvc.instant('HELP_MENU.ALT_SCROLL_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.ZOOM_IN_OUT_DESCRIPTION'
      ),
      [this.translateSvc.instant('HELP_MENU.HOLD_ON_MOUSE_CLICK_DESCRIPTION')]:
        this.translateSvc.instant('HELP_MENU.MOVE_ELEMENTS_ON_CANVAS_DESCRIPTION'),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_P_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.PRINT_DIAGRAM_DESCRIPTION')
    };

    this.switchToEditHint = {
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_E_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_E_DESCRIPTION')
    };

    this.switchToViewHint = {
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_E_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_E_DESCRIPTION_TO_VIEW')
    };

    this.editModeHelpList = {
      [this.translateSvc.instant('HELP_MENU.TAB_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.TAB_DESCRIPTION'
      ),
      [this.translateSvc.instant('HELP_MENU.TAB_SHIFT_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.TAB_SHIFT_DESCRIPTION'
      ),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_C_V_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_C_V_DESCRIPTION'),
      [this.translateSvc.instant('HELP_MENU.DELETE_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.DELETE_DESCRIPTION'
      ),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}SELECT_ELEMENT_ON_CANVAS_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.SELECT_ELEMENT_ON_CANVAS_DESCRIPTION'),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}SELECT_EXCEPT_FLOWLINES_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.SELECT_EXCEPT_FLOWLINES_DESCRIPTION'),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_Z_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_Z_DESCRIPTION'),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_Y_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_Y_DESCRIPTION'),
      [this.translateSvc.instant(`HELP_MENU.${osPrefix}CONTROL_L_SHORTCUT`)]:
        this.translateSvc.instant('HELP_MENU.CONTROL_L_DESCRIPTION'),
      [this.translateSvc.instant('HELP_MENU.CREATE_ACTIVITY_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.CREATE_ACTIVITY_DESCRIPTION'
      ),
      [this.translateSvc.instant('HELP_MENU.CREATE_TEXT_SHORTCUT')]: this.translateSvc.instant(
        'HELP_MENU.CREATE_TEXT_DESCRIPTION'
      ),
      [this.translateSvc.instant('HELP_MENU.CREATE_STICKY_NOTE_SHORTCUT')]:
        this.translateSvc.instant('HELP_MENU.CREATE_STICKY_NOTE_DESCRIPTION')
    };
  }

  keepOrder = (a, b) => {
    return a;
  };
}
